/**
 * Leaguemate Scouting — S3.5 positional lean (pure, no I/O).
 *
 * Roster position mix (RB/WR/TE share of skill players, pooled over the tx-set
 * rosters reused from S2) + rookie-draft positional bias (from rookie drafts,
 * settings.rounds ≤ N). Baseline medians (§7c): RB 35% / WR 46% / TE 21%.
 *
 * Reach-vs-consensus is DEFERRED (§7d — consensus base too thin from the
 * sim-floored capped pull). Not built here.
 */

import {
  POS_RB_HEAVY_MIN,
  POS_ZERO_RB_MAX,
  POS_WR_HEAVY_MIN,
  POS_TE_BELIEVER_MIN,
} from "./scoutConfig.js";

/** RB/WR/TE share of SKILL players (QB excluded — baseline sums to ~100%). */
export function computeRosterMix(rosterRows, playersMap = {}) {
  const counts = { QB: 0, RB: 0, WR: 0, TE: 0 };
  for (const lg of rosterRows || []) {
    for (const pid of lg.players || []) {
      const pos = (playersMap[pid] || {}).position;
      if (pos && counts[pos] != null) counts[pos]++;
    }
  }
  const skillTotal = counts.RB + counts.WR + counts.TE;
  const pct = (n) => (skillTotal ? Math.round((1000 * n) / skillTotal) / 10 : 0);
  return { counts, skillTotal, rbShare: pct(counts.RB), wrShare: pct(counts.WR), teShare: pct(counts.TE) };
}

/** Positional distribution of the manager's rookie-draft picks. */
export function computeRookieBias(pickRows) {
  const counts = { QB: 0, RB: 0, WR: 0, TE: 0 };
  let total = 0;
  for (const lg of pickRows || []) {
    for (const rp of lg.rookiePicks || []) {
      const pos = rp.position;
      if (pos && counts[pos] != null) { counts[pos]++; total++; }
    }
  }
  const pct = (n) => (total ? Math.round((1000 * n) / total) / 10 : 0);
  return { counts, total, qbShare: pct(counts.QB), rbShare: pct(counts.RB), wrShare: pct(counts.WR), teShare: pct(counts.TE) };
}

export function computePositional(rosterRows, pickRows, playersMap = {}) {
  const rosterMix = computeRosterMix(rosterRows, playersMap);
  const rookieBias = computeRookieBias(pickRows);
  const flags = [];
  if (rosterMix.skillTotal > 0) {
    if (rosterMix.rbShare >= POS_RB_HEAVY_MIN) flags.push("RB-heavy");
    if (rosterMix.rbShare <= POS_ZERO_RB_MAX) flags.push("zero-RB-ish");
    if (rosterMix.wrShare >= POS_WR_HEAVY_MIN) flags.push("WR-heavy");
    if (rosterMix.teShare >= POS_TE_BELIEVER_MIN) flags.push("TE-believer");
  }
  return { rosterMix, rookieBias, flags };
}
